import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAttributeDefinitions,
  getContactAttributeValues,
  upsertContactAttributeValue,
  clearContactAttributeValue,
  addContactAttributeValue,
  removeContactAttributeValue,
} from "../../services/contactAttributes.service";
import { getDisplayValue } from "../../types/contactAttribute.types";
import { useToast } from "../ui/Toast";
import type { Contact } from "../../types/contact.types";
import type { UUID } from "../../types/common.types";
import type {
  AttributeDefinition,
  ContactAttributeValue,
  AttributeValueInput,
} from "../../types/contactAttribute.types";

interface ContactCustomAttributesSectionProps {
  contactId: UUID;
  contact: Contact;
}

function isApplicable(def: AttributeDefinition, contact: Contact): boolean {
  if (!def.is_active) {
    return false;
  }
  switch (def.applicable_to) {
    case "all":
      return true;
    case "contact_type": {
      if (!def.applicable_contact_types?.length) {
        return false;
      }
      let types: string[] = [];
      if (contact.contact_types?.length) {
        types = contact.contact_types;
      } else if (contact.contact_type) {
        types = [contact.contact_type];
      }
      return def.applicable_contact_types.some((t) =>
        types.map((x) => x.toLowerCase()).includes(t.toLowerCase()),
      );
    }
    case "member_account":
      return contact.is_primary_member === true;
    case "member_individual":
      return !!(contact.is_usls_member || contact.member_status);
    default:
      return false;
  }
}

function buildValueInput(
  raw: string,
  def: AttributeDefinition,
): AttributeValueInput | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  switch (def.data_type) {
    case "text":
    case "select":
      return { value_text: trimmed };
    case "number": {
      const n = parseFloat(trimmed);
      return isNaN(n) ? null : { value_number: n };
    }
    case "boolean":
      return { value_boolean: trimmed === "true" };
    case "date":
      return { value_date: trimmed };
    default:
      return null;
  }
}

function AttributeInput({
  def,
  value,
  onChange,
}: {
  def: AttributeDefinition;
  value: string;
  onChange: (v: string) => void;
}) {
  const inputClass =
    "w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500";
  switch (def.data_type) {
    case "select":
      return (
        <select
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— select —</option>
          {(def.select_options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    case "boolean":
      return (
        <select
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— not set —</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    case "number":
      return (
        <input
          type="number"
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "date":
      return (
        <input
          type="date"
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      return (
        <input
          type="text"
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

function valueToString(
  v: ContactAttributeValue,
  def: AttributeDefinition,
): string {
  switch (def.data_type) {
    case "boolean":
      return v.value_boolean !== null && v.value_boolean !== undefined
        ? String(v.value_boolean)
        : "";
    case "number":
      return v.value_number !== null && v.value_number !== undefined
        ? String(v.value_number)
        : "";
    case "date":
      return v.value_date ?? "";
    default:
      return v.value_text ?? "";
  }
}

function SingleValueRow({
  def,
  existing,
  contactId,
}: {
  def: AttributeDefinition;
  existing: ContactAttributeValue | undefined;
  contactId: UUID;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const upsertMutation = useMutation({
    mutationFn: (input: AttributeValueInput) =>
      upsertContactAttributeValue(contactId, def.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contact-attrs", contactId],
      });
      setEditing(false);
    },
    onError: () => showToast("error", "Failed to save attribute"),
  });

  const clearMutation = useMutation({
    mutationFn: () => clearContactAttributeValue(contactId, def.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contact-attrs", contactId],
      });
      setEditing(false);
    },
    onError: () => showToast("error", "Failed to clear attribute"),
  });

  const handleEdit = () => {
    setDraft(existing ? valueToString(existing, def) : "");
    setEditing(true);
  };

  const handleSave = () => {
    if (!draft.trim()) {
      if (existing) {
        clearMutation.mutate();
      } else {
        setEditing(false);
      }
      return;
    }
    const input = buildValueInput(draft, def);
    if (!input) {
      showToast("error", "Invalid value");
      return;
    }
    upsertMutation.mutate(input);
  };

  const displayText = existing ? getDisplayValue(existing, def) : "";

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <AttributeInput def={def} value={draft} onChange={setDraft} />
        </div>
        <button
          onClick={handleSave}
          disabled={upsertMutation.isPending || clearMutation.isPending}
          className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
        >
          Save
        </button>
        <button
          onClick={() => setEditing(false)}
          className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group min-h-[1.5rem]">
      <span className="text-sm text-gray-900">
        {displayText || (
          <span className="text-gray-400 italic">
            {def.slug?.startsWith("pardot_account_") ? "No Pardot value" : "—"}
          </span>
        )}
      </span>
      <button
        onClick={handleEdit}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary-600"
        title="Edit"
      >
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536M9 11l6-6 3 3-6 6H9v-3z"
          />
        </svg>
      </button>
    </div>
  );
}

function MultiValueRows({
  def,
  values,
  contactId,
}: {
  def: AttributeDefinition;
  values: ContactAttributeValue[];
  contactId: UUID;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [addDraft, setAddDraft] = useState("");
  const [adding, setAdding] = useState(false);

  const addMutation = useMutation({
    mutationFn: (input: AttributeValueInput) =>
      addContactAttributeValue(contactId, def.id, input, values.length),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-attrs", contactId] });
      setAddDraft("");
      setAdding(false);
    },
    onError: () => showToast("error", "Failed to add value"),
  });

  const removeMutation = useMutation({
    mutationFn: (valueId: UUID) => removeContactAttributeValue(valueId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["contact-attrs", contactId] }),
    onError: () => showToast("error", "Failed to remove value"),
  });

  const handleAdd = () => {
    if (!addDraft.trim()) {
      return;
    }
    const input = buildValueInput(addDraft, def);
    if (!input) {
      showToast("error", "Invalid value");
      return;
    }
    addMutation.mutate(input);
  };

  return (
    <div className="space-y-1">
      {values.map((v) => (
        <div key={v.id} className="flex items-center gap-2 group">
          <span className="text-sm text-gray-900">
            {getDisplayValue(v, def)}
          </span>
          <button
            onClick={() => removeMutation.mutate(v.id)}
            disabled={removeMutation.isPending}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 disabled:opacity-50"
            title="Remove"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ))}
      {adding ? (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <AttributeInput def={def} value={addDraft} onChange={setAddDraft} />
          </div>
          <button
            onClick={handleAdd}
            disabled={addMutation.isPending}
            className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            Add
          </button>
          <button
            onClick={() => {
              setAdding(false);
              setAddDraft("");
            }}
            className="text-xs text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add value
        </button>
      )}
    </div>
  );
}

const ContactCustomAttributesSection: React.FC<
  ContactCustomAttributesSectionProps
> = ({ contactId, contact }) => {
  const { data: allDefs = [] } = useQuery({
    queryKey: ["attr-defs"],
    queryFn: getAttributeDefinitions,
  });

  const { data: allValues = [] } = useQuery({
    queryKey: ["contact-attrs", contactId],
    queryFn: () => getContactAttributeValues(contactId),
    enabled: !!contactId,
  });

  const applicableDefs = allDefs.filter((d) => isApplicable(d, contact));

  if (applicableDefs.length === 0) {
    return null;
  }

  // Pardot staleness badge — surfaces contacts.pardot_last_synced_at
  // when any visible attribute is Pardot-sourced (slug starts with
  // 'pardot_account_'). Operators reading the contact see "this data
  // is from the last Pardot webhook" so segment-count drift isn't
  // surprising. Audit AI 2026-05-23 Option H.
  const hasPardotAttribute = applicableDefs.some((d) =>
    d.slug.startsWith("pardot_account_"),
  );
  const pardotSyncedAt = contact.pardot_last_synced_at;
  const formattedSynced = pardotSyncedAt
    ? new Date(pardotSyncedAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="border-t border-gray-100 pt-4">
      <div className="flex items-baseline justify-between mb-4 gap-2 flex-wrap">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Custom Attributes
        </h3>
        {hasPardotAttribute && (
          <span
            className="text-xs text-gray-500"
            title="Pardot-sourced attributes are only as fresh as the last webhook delivery for this contact."
          >
            {formattedSynced
              ? `Pardot synced: ${formattedSynced}`
              : "Pardot synced: never"}
          </span>
        )}
      </div>
      <dl className="space-y-4">
        {applicableDefs.map((def) => {
          const values = allValues.filter((v) => v.attribute_id === def.id);
          return (
            <div key={def.id}>
              <dt className="text-sm font-medium text-gray-500 mb-0.5">
                {def.name}
                {def.slug?.startsWith("pardot_account_") && (
                  <span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-gray-400">
                    (Pardot-only)
                  </span>
                )}
              </dt>
              <dd>
                {def.is_multi_valued ? (
                  <MultiValueRows
                    def={def}
                    values={values}
                    contactId={contactId}
                  />
                ) : (
                  <SingleValueRow
                    def={def}
                    existing={values[0]}
                    contactId={contactId}
                  />
                )}
                {def.slug === "pardot_account_sms_opt_out" && (
                  <p className="mt-1 text-xs italic text-gray-400">
                    Raw Pardot account field. The CRM's consolidated SMS opt-out
                    status (shown under the contact's Communication section) is
                    the source of truth for sends and may be opted out via CRM
                    suppression or SMS opt-in even when this is blank.
                  </p>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
};

export default ContactCustomAttributesSection;
